<template>
	<div>
		<ProjectTitle :project="projectInfo" v-if="project !== null" :pageType="'data-dive'" :readOnly="true" :showFeatures="['locationName']" :ignoreFeatures="['locationName']">
			<template v-slot:right>
				<div :class="{ recheck: true, innactive: !projectInfo || !projectInfo.canRecheckGoogle }" v-if="showRecheck">
					<span
						:class="{ 'large-button': true, mini: true, 'recheck-btn': true, 'no-hover': !!projectInfo.googleReCheck }"
						v-if="projectInfo"
						@click="reCheckData"
						v-html="projectInfo.googleReCheck ? 'Re-checking' : reCheckDataClicked ? 'One Moment...' : 'Re-check Data'"
					/>

					<p v-html="lastCheckedInfoTable.short" />
					
				</div>
				<div :class="{ 'insights-btn-wrap': true }">
					<span :class="{'large-button':true,insights:true,mini:true}" @click="openInsights" v-if="project && googleAccountConnected"><Icon type="insights" class="svg insights white" /> &nbsp;<span>Insights</span></span>
				</div>
				
			</template>
		</ProjectTitle>

		<MinimizeList
			:listItems="minimizedList"
			:selected="showMinimized[type]"
			@minimize="minimize"
			v-if="project && googleAccountConnected & !newAndQueued"
		>
			<template v-slot:url-keyword>
				<div class="url-keyword-buttons" id="data-dive-url-keyword-buttons">
					<h2 class="view">View By</h2>
					<span :class="{ 'large-button': true, mini: true, active: type === 'url' }" @click="switchtype('url')">URL</span>
					<span :class="{ 'large-button': true, mini: true, active: type === 'keyword' }" @click="switchtype('keyword')">Keyword</span>
				</div>
			</template>

			<template v-slot:frequency>
				<div class="frequency-setters" v-if="hasDays && !!project" id="data-dive-frequency-setters">
					<Switcher
						:sameColour="true"
						v-model="showDifferences"
						:switcherLabel="'Compare previous'"
						:flipped="true"
						class="differences-switcher"
					/>
					<span
						v-for="(frequency, frequencyIndex) in frequencies"
						:key="'frequency-' + frequencyIndex"
						:class="{ selected: selectedFrequency === frequency }"
						@click="selectedFrequency = frequency"
						v-html="frequency + ' Days'"
					/>
					<p class="last-checked" v-html="lastCheckedInfo" v-if="lastCheckedInfo" />
				</div>
			</template>
		</MinimizeList>

		<section class="outer-pad">
			<PageMessage
				:invert="true"
				class="still-gathering error"
				v-if="project !== null && projectAdmin && !!projectInfo.googleAccountInvalid && !reconnectErrored"
			>
				<strong>{{ projectInfo.url }}</strong> has had errors when trying to pull data from the attached Google Search Console/Analytics
				accounts, <span class="reconnect" @click="reconnectErrored = true">please click here to re-connect</span>. If you continue to have issues please get in touch with us.
			</PageMessage>

			<PageMessage :invert="true" class="still-gathering error" v-if="project !== null && !projectAdmin && !!projectInfo.googleAccountInvalid">
				<strong>{{ projectInfo.url }}</strong> has had errors when trying to pull data from the attached Google Search Console/Analytics
				accounts, this must be done by the project owner.
			</PageMessage>

			<Modal 
				v-if="project !== null && ( !googleAccountConnected || reconnectErrored ) && table[type].loading === false && projectAdmin"
				:noModal="!reconnectErrored"
				:width="700"
				@close="reconnectErrored = false"
			>

				<template v-slot:content>

					<GoogleConnect
						:projectIds="[project]"
						:domain="projectDomain"
						:confirmUnder="true"
						:showLoading="true"
						:reconnectErrored="reconnectErrored||!googleAccountConnected"
						scope="https://www.googleapis.com/auth/webmasters.readonly https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/userinfo.email"
						clientId="444580885070-gc0jr5mklt4keng77gum1saqme2isr9c.apps.googleusercontent.com"
						@complete="googleConnectComplete"
						:externalError="googleError"
						class="google-connect-data-dive"
					/>

				</template>

			</Modal>

			<StatsOverview 
				v-if="overviewStats"
				:stats="overviewStats"
			/>

			<GraphWrap
				:chartdata="theChart.data"
				:options="theChart.options"
				:graphKey="graphKey"
				@label-click="labelClick"
				size="full"
				ratio="seventy-thirty"
				v-if="showGraph"
				:class="{ 'graph-margin-bottom': showOpportunityFilters }" 
				id="the-graph"
			>
				<template slot="above-graph">
					<span class="svg minimize" title="Minimize" @click="minimize('graph',true)"
						><font-awesome-icon icon="down-left-and-up-right-to-center"
					/></span>
					<div class="graph-checkbox-wrap" id="data-dive-graph-checks">
						<FormInput type="checkbox" v-model="graphLines" :items="graphLineTickBoxes" :inline="true" :noMarginBottom="true" />
					</div>
				</template>
				<template v-slot:below-graph>
					<EventActions 
						:project="projectInfo" 
						:events="events" 
						:showEvents="showEventsModal"
						@reload-chart="loadGraph" 
						@close="showEventsModal = false"
						class="event-actions" 
					/>
				</template>
			</GraphWrap>

			<OpportunityFilters
				:type="type"
				:projectInfo="projectInfo"
				v-if="showOpportunityFilters"
				:filters="opportunityFilters[type]"
				@loadFilters="loadFieldsAndFilters"
				@searchFilters="searchFilters"
				@brandIgnoreSettingChange="brandIgnoreSettingChange"
				@minimize="minimize('filters',true)"
				:selectedIndex="selectedFilter.index"
				:key="type + opportunityFiltersKey"
				ref="opportunityFilters"
				id="the-filters"
			/>

			<PageMessage
				v-if="
					!!projectInfo &&
					project !== null &&
					!googleAccountConnected &&
					table[type].loading === false &&
					!projectAdmin &&
					!projectInfo.googleAccountInvalid
				"
				class="still-gathering"
			>
				{{ projectName }} doesn't currently have a Google Account associated with it, this is required for the Data Dive area to work and must
				be done by the project owner.
			</PageMessage>

			<PageMessage v-if="newAndQueued" :invert="true" class="still-gathering">
				{{ projectName }} is in the Data Dive queue to be processed...
			</PageMessage>

			<PageMessage :invert="true" class="still-gathering" v-if="stillGathering">
				We are still gathering some of the data for this project...
			</PageMessage>

			<PageMessage :invert="true" class="still-gathering" v-if="projectInfo && projectInfo.googleReCheck">
				We are re-checking the data for this project (some columns may show with incorrect counts whilst this happens)...
			</PageMessage>

			<p class="last-checked for-table" v-if="showGraph">
				<span class="description">Showing {{ selectedFrequency }} days of data</span
				><span class="checked" v-if="lastCheckedInfoTable.full">{{ lastCheckedInfoTable.full }}</span>
			</p>

			<Table
				ref="mainTable"
				id="t0"
				v-if="project !== null && googleAccountConnected"
				class="the-table"
				:headings="tableHeadings"
				:items="tableItems"
				:pages="table[type].pages"
				:page="table[type].page"
				:count="table[type].count"
				:sorted="table[type].sorted"
				:emptyLabel="emptyLabel"
				:loading="table[type].loading"
				:loadingText="loadingText"
				:name="'data-dive--' + type"
				:key="'table' + type + tableKey"
				:hadKeyUpdate="tableRemounted[type]"
				:title="tableTitle.type"
				:searchMultiple="true"
				:searchMultipleDescription="searchDescription"
				:searchMultipleToolTipText="searchDescriptionToolTipText"
				:searchMultipleToolTipPreText="searchDescriptionToolTipPreText"
				searchMultipleType="data-dive"
				:searchMultipleTitle="searchTitle"
				:searchMultipleComparisons="comparisons"
				:searchSetup="searchSetup[type]"
				:searchMultipleButtonLabel="'Click to add advanced filter options'"
				:searchSetupMultiple="searchSetupMultiple"
				searchMultipleHiddenByDefault="all"
				:searchMultipleValues="table[type].search"
				:searchMultipleOrderByList="orderByList"
				:searchMultipleLastUpdate="table[type].lastUpdate"
				:searchMultipleOrderByMax="1"
				:triggerSomething="triggerSomething[type]"
				:filterQueryTitle="filterQueryTitle[type]"
				:filterQueryDescriptionOverride="filterQueryDescription[type]"
				:showSaveFilter="showSaveFilter"
				:meta="tableMeta"
				:queryKey="tableQueryKey"
				:hideSearch="false"
				@selected="keywordActions"
				@viewKeywords="viewKeywords"
				@investigate="investigate"
				@pagination="pagination"
				@sort="sort"
				@search="search"
				@addToProject="addToProject"
				@runReport="runReport"
				@reRunReport="reRunReport"
				@trigger-complete="triggerSomething[type] = ''"
				@closeSearchMultiple="closeSearchMultiple"
				@filtersChanged="filtersChanged"
				@saveFilter="saveFilter"
				@tableMounted="tableMounted"
				:showCSV="true"
				@csv="download('csv')"
				:showPDF="false"
				@pdf="download('pdf')"
			>
				<template slot="after-filter-query" v-if="showHighlightText[theType]">
					<p class="highlight-text" v-html="highlightText[theType]" />
				</template>
			</Table>
		</section>

		<Modal 
			v-if="url!==''"
			:width="1200"
			@close="close"
			:forceTitle="true"
			:hasHeader="true"
		>

			<template v-slot:title>
				<a class="modal-title-wrap link" :href="url" :title="url" v-if="!table.urlKeyword.loading" target="_blank">{{ minUrl }} &nbsp;<font-awesome-icon icon="up-right-from-square" :style="{ color: '#ffffff' }"/></a>
			</template>

			<template v-slot:header>
				<span
					v-if="!table.urlKeyword.loading && tableItemsUrlKeyword.length > 0"
					:class="{
						'large-button': true,
						'process-reports': true,
						'flip-colours': true,
						mini: true,
						disabled: generateReportButtonDisabled,
					}"
					@click="runReport({ _id: 'all' }, true, 'urlKeyword')"
					v-html="generateReportButtonLabel"
				/>
			</template>

			<template v-slot:content>

				<MinimizeList
					:listItems="minimizedList"
					:selected="showMinimized.urlKeyword"
					:inModal="true"
					@minimize="minimize"
					v-if="project && googleAccountConnected"
				/>

				<OpportunityFilters2
					type="urlKeyword"
					:projectInfo="projectInfo"
					v-if="fetchedProject && googleAccountConnected && !!showMinimized.urlKeyword.filters"
					:url="url"
					:filters="opportunityFilters.urlKeyword"
					@loadFilters="loadFieldsAndFilters"
					@searchFilters="searchFilters"
					@minimize="minimize('filters',true)"
					@brandIgnoreSettingChange="brandIgnoreSettingChange"
					:selectedIndex="urlKeywordSelectedFilter.index"
					:hasPageReports="table.urlKeyword.hasPageReports"
					:key="'urlKeyword' + opportunityFiltersKey"
					:isInModal="true"
				/>

				<div class="frequency-setters in-modal" v-if="hasDays">
					<Switcher
						:sameColour="true"
						v-model="showDifferences"
						:switcherLabel="'Compare previous'"
						class="differences-switcher"
						:flipped="true"
					/>
					<span
						v-for="(frequency, frequencyIndex) in [7, 30, 90]"
						:key="'frequency2-' + frequencyIndex"
						:class="{ selected: selectedFrequency === frequency }"
						@click="selectedFrequency = frequency"
						v-html="frequency + ' Days'"
					/>
				</div>

				<StatsOverview 
					v-if="!table.urlKeyword.loading&&urlStats.stats.length"
					:stats="urlStats"
					:full="false"
				/>

				<Table
					id="t1"
					loadingText="Fetching keywords..."
					searchMultipleHiddenByDefault="all"
					:headings="tableHeadingsUrlKeyword"
					:items="tableItemsUrlKeyword"
					:pages="table.urlKeyword.pages"
					:page="table.urlKeyword.page"
					:title="tableTitle.urlKeyword"
					:count="table.urlKeyword.count"
					:sorted="table.urlKeyword.sorted"
					:loading="table.urlKeyword.loading"
					:key="'table-urlKeyword' + tableKey"
					:hadKeyUpdate="tableRemounted.urlKeyword"
					:name="'data-dive--urlKeyword'"
					:searchMultiple="true"
					:searchMultipleValues="table.urlKeyword.search"
					:searchMultipleDescription="searchDescription"
					:searchMultipleToolTipText="searchDescriptionToolTipText"
					:searchMultipleToolTipPreText="searchDescriptionToolTipPreText"
					searchMultipleType="data-dive"
					:searchMultipleTitle="searchTitle"
					:searchMultipleComparisons="comparisons"
					:searchMultipleOrderByList="orderByList"
					:searchMultipleOrderByMax="1"
					:searchMultipleLastUpdate="table.urlKeyword.lastUpdate"
					:searchMultipleButtonLabel="'Click to add advanced filter options'"
					:searchSetup="searchSetup.urlKeyword"
					:searchSetupMultiple="searchSetupMultiple"
					:triggerSomething="triggerSomething.urlKeyword"
					:filterQueryTitle="filterQueryTitle.urlKeyword"
					:filterQueryDescriptionOverride="filterQueryDescription.urlKeyword"
					:showSaveFilter="showSaveFilter"
					:meta="tableMeta"
					:queryKey="tableQueryKey"
					:hideSearch="false"
					@selected="keywordActions"
					@addToProject="addToProject"
					@runReport="runReportUrlKeyword"
					@reRunReport="reRunReport"
					@investigate="investigate"
					@pagination="pagination($event, 'urlKeyword')"
					@sort="sort($event, 'urlKeyword')"
					@search="search($event, 'urlKeyword')"
					@trigger-complete="triggerSomething.urlKeyword = ''"
					@closeSearchMultiple="closeSearchMultiple"
					@saveFilter="saveFilter"
					:showCSV="true"
					@csv="download('csv')"
					:showPDF="false"
					@pdf="download('pdf')"
				>
					<template slot="after-filter-query" v-if="showHighlightText.urlKeyword">
						<p class="highlight-text" v-html="highlightText.urlKeyword" />
					</template>
				</Table>

			</template>

			<template v-slot:footer-inner>
				<ActionsBar v-if="keywordsSelectedProcessed.ids.length > 0 && url !== ''" :full="false">
					<span
						class="action-button"
						v-html="
							'Track ' +
								keywordsSelectedProcessed.keywords.length +
								' keyword' +
								(keywordsSelectedProcessed.keywords.length > 1 ? 's' : '')
						"
						@click="showKeywordModal = true"
						v-if="keywordsSelectedProcessed.keywords.length > 0"
					/>

					<span
						class="action-button"
						v-html="
							'Investigate ' +
								keywordsSelectedProcessed.ids.length +
								' keyword' +
								(keywordsSelectedProcessed.ids.length > 1 ? 's' : '')
						"
						@click="investigate()"
					/>
				</ActionsBar>
			</template>

		</Modal>

		<ActionsBar v-if="keywordsSelectedProcessed.ids.length > 0 && url === '' && addToProjectProjectIds.length === 0">
			<span
				class="action-button"
				v-html="
					'Track ' + keywordsSelectedProcessed.keywords.length + ' keyword' + (keywordsSelectedProcessed.keywords.length > 1 ? 's' : '')
				"
				@click="showKeywordModal = true"
				v-if="type === 'keyword' && keywordsSelectedProcessed.keywords.length > 0"
			/>

			<span
				class="action-button"
				v-html="
					'Investigate ' +
						keywordsSelectedProcessed.ids.length +
						' ' +
						(theType === 'url' ? 'URL' : 'keyword') +
						(keywordsSelectedProcessed.ids.length > 1 ? 's' : '')
				"
				@click="investigate()"
			/>
		</ActionsBar>

		<Keywords
			v-if="showKeywordModal"
			@close="closeKeywordModal"
			:project="addToProjectProjectIds.length ? addToProjectProjectIds : project"
			:internal="true"
			:keywords="keywordsSelectedProcessed.keywords"
		/>

		<Investigate
			v-if="showInvestigate"
			:theInvestigations="investigations"
			:filter="selectedFilterUniqueName"
			:type="theType"
			@close="closeInvestigateModal"
			:project="project"
			:isDataDive="true"
		/>

		<ReportOptions
			:project="project"
			:pages="table[theType].pages"
			:page="table[theType].page"
			:projectName="projectName"
			:type="showReport === 'csv' || showReport === 'pdf' ? showReport : ''"
			v-if="showReport"
			extraText="<strong>Queries with filtering will be limited to 1000, if there are multiple filters some will be limited to 200.</strong>"
			@download="closeReportOptions"
			@close="closeReportOptions"
		/>


		<InsightsModal 
			@close="closeInsights"
			:projectId="project"
			:title="insightsTitle"
			v-if="showInsights"
			source="data-dive"
		/>

	</div>
</template>

<script>
	import Vue from "vue";
	import { mapMutations, mapState, mapActions } from "vuex";
	import ApiService from "./../services/api.service";
	import general from "./../utils/general";
	import { TokenService } from "./../services/storage.service";
	import Table from "./../components/Table.vue";
	import GoogleConnect from "./../components/GoogleConnect.vue";
	import LinkWithIcon from "./../components/Display/LinkWithIcon.vue";
	import TableButton from "./../components/Display/TableButton.vue";
	import TableCurrentPosition from "./../components/Display/TableCurrentPosition.vue";
	import TableDataDiveReport from "./../components/Display/TableDataDiveReport.vue";
	import ActionsBar from "./../components/Display/ActionsBar.vue";
	import Keywords from "./../components/Keywords.vue";
	import FormInput from "./../components/Form/Input.vue";
	import OpportunityFilters from "./../components/OpportunityFilters.vue";
	import OpportunityFilters2 from "./../components/OpportunityFilters.vue";
	import PageReport from "./../components/PageReport.vue";
	import Investigate from "./../components/Investigate.vue";
	import PageMessage from "./../components/PageMessage.vue";
	import RankUpDown from "./../components/Display/RankUpDown.vue";
	import Switcher from "./../components/Switcher.vue";
	import MinimizeList from "./../components/MinimizeList.vue";
	import GraphWrap from "./../components/Data/GraphWrap.vue";
	import ProjectTitle from "./../components/Display/ProjectTitle.vue";
	import ReportOptions from "./../components/ReportOptions.vue";
	import EventActions from "./../components/EventActions.vue";
	import InsightsModal from "./../components/Display/InsightsModal.vue";
	import Icon from "./../components/Display/Icon.vue";
	import Modal from "./../components/Display/Modal.vue";
	import StatsOverview from "./../components/Data/StatsOverview.vue";
	export default {
		name: "DataDive",
		components: {
			Table,
			ProjectTitle,
			GoogleConnect,
			Keywords,
			FormInput,
			OpportunityFilters,
			OpportunityFilters2,
			ActionsBar,
			Investigate,
			PageMessage,
			TableDataDiveReport,
			Switcher,
			MinimizeList,
			GraphWrap,
			ReportOptions,
			EventActions,
			Icon,
			InsightsModal,
			Modal,
			StatsOverview,
		},
		data() {
			return {
				showInsights: false,
				init: {
					loadedFilters: false,
					loadedTable: false,
					loadedGraph: false,
					loadedProject: false
				},
				selectedUrlId: null,
				selectedURLOverview: null,
				showEventsModal: false,
				frequencies: [7, 30, 90],
				graphKey: 0,
				graphInfo: {},
				graphLoaded: false,
				graphLines: ["sessions", "impressions"],
				addingNewFilter: true,
				showKeywordModal: false,
				showPageReports: false,
				viewReports: [],
				showDifferences: true,
				projectInfo: {},
				fetchedProject: false,
				brandIgnoreSetting: false,
				showInvestigate: false,
				googleError: "",
				urlActual: null,
				searchDescriptionOverride: "",
				searchTitleOverride: "",
				showSaveFilter: true,
				editFilterInfo: null,
				opportunityFiltersKey: 0,
				tableQueryKey: 0,
				tableKey: 0,
				chartData: [],
				selectedFrequency: 90,
				hasDays: false,
				loadedOpportunitiesAfterProjects: false,
				showToolTip: false,
				reconnectErrored: false,
				showReport: false,
				pdfTitle: "",
				addToProjectProjectIds: [],
				zoom: {
					min: 1,
					max: 100,
				},
				projects: {
					project: null,
					shared: [],
				},
				url: "",
				keywordsSelected: {
					keyword: [],
					url: [],
					urlKeyword: [],
				},
				opportunityFilters: {},
				opportunityFiltersLoaded: false,
				filters: {},
				filter: {
					type: null,
					comparison: null,
					value: "",
				},
				comparisons: {},
				fields: {},
				showMinimized: {
					url: {
						graph: true,
						filters: true,
					},
					keyword: {
						graph: true,
						filters: true,
					},
					urlKeyword: {},
				},
				triggerSomething: {
					url: "",
					keyword: "",
					urlKeyword: "",
				},
				hasHashFilter: false,
				clearParent: false,
				investigations: [],
				loadProjectTimeout: 0,
				loadOpportunitiesTimeout: 0,
				reCheckDataClicked: false,
				forceReconnect: false,
				filterQueryTitle: {
					url: "",
					keyword: "",
					urlKeyword: "",
				},
				filterQueryDescription: {
					url: "",
					keyword: "",
					urlKeyword: "",
				},
				highlightText: {
					keyword: "",
					url: "",
					urlKeyword: "",
				},
				tableRemounted: {
					url: false,
					keyword: false,
					urlKeyword: false,
				},
				table: {
					url: {
						loading: true,
						pages: 1,
						page: 1,
						count: 0,
						items: [],
						search: [],
						search2: null,
						firstLoad: true,
						lastUpdate: 0,
						sorted: {
							keywordCount: -1,
						},
					},
					keyword: {
						loading: true,
						pages: 1,
						page: 1,
						count: 0,
						items: [],
						search: [],
						search2: null,
						firstLoad: true,
						lastUpdate: 0,
						sorted: {
							clicks: -1,
						},
					},
					urlKeyword: {
						loading: true,
						pages: 1,
						page: 1,
						count: 0,
						items: [],
						search: [],
						search2: null,
						firstLoad: true,
						lastUpdate: 0,
						sorted: {
							clicks: -1,
						},
					},
				},
			};
		},
		watch: {
			projectInfo: {
				deep: true,
				handler(newVal) {
					this.initLoaded("loadedProject");
					let n = typeof newVal.brandIgnoreSetting !== "undefined" ? !!newVal.brandIgnoreSetting : null;

					if (n !== null && n !== this.brandIgnoreSetting && this.initFinished) {
						this.brandIgnoreSetting = n;

						this.loadOpportunities(this.theType, null, null, "watch projectInfo");
					}
				},
			},
			selectedFrequency: {
				handler() {
					if (this.hasHashFilter) return;

					this.loadOpportunities(this.theType, null, null, "watch selectedFrequency");
					if ( this.theType === 'urlKeyword' ) {
						this.loadOpportunities('url', null, null, "watch selectedFrequency");
					}
				},
			},
			projectListLoaded: {
				handler() {
					if (!!this.projectListLoaded && this.loadedOpportunitiesAfterProjects) {
						this.loadedOpportunitiesAfterProjects = false;

						if ( !!this.fetchedProject ) this.initLoaded("loadedProject");

						this.loadOpportunities();

						this.loadOpportunities(this.theType, null, null, "watch projectListLoaded");
					}
				},
			},
		},
		computed: {
			...mapState("general", ["tableHeadingVisibility", "tableSortingInfo", "dateTimeFormat", "projectList", "projectListLoaded"]),

			overviewStats() {

				if ( this.projectInfo.totals ) {

					let fields = ['sessions', 'users', 'bounceRate', 'avgSessionDuration', 'clicks', 'ctr', 'impressions', 'position'], 
					stats = [];

					fields.forEach( function(field) {

						let cur = this.projectInfo.totals[field+this.selectedFrequency], 
							his = this.projectInfo.totals[field+this.selectedFrequency+'History'], 
							outcome = 'good',
							direction = '+',
							percent,
							diff = 0;
	
						if ( his ) diff = Math.abs( cur - his );


						if ( diff > 0 ) {

							percent = general.percentDiff(cur,his);

							if ( cur < his ) {

								percent = ( diff / cur ) * 100;
								direction = '-';
								outcome = 'bad';

							}

						}

						if ( this.fieldMeta[field].lessIsBetter ) {
							outcome = outcome === 'good' ? 'bad' : 'good';
						}

						if ( field !== 'ctr' ) {

							cur = general.formatNumber(cur,10000);
							his = general.formatNumber(his,10000);

						}
						else {

							cur *= 100;
							his *= 100;
							cur = cur.toFixed(2) + '%';
							his = his.toFixed(2) + '%';

						}

						let stat = { 
							type: field,
							label: this.fieldMeta[field].label,
							value: cur
						};

						if ( diff ) {
							stat.change = { 
								value: Math.round(percent), 
								outcome: outcome, 
								direction: direction, 
								type: 'percentage', 
								oldValue: 'Previously ' + this.projectInfo.totals[field+this.selectedFrequency+'History'] 
							}
						}

						stats.push(stat);

					}.bind(this));

					return {
						'stats': stats 
					};

				}

				return false;
			},

			insightsTitle () {

				return 'Data Dive Insights - ' + this.projectName;

			},

			fieldMeta() {
				return {
					sessions: { label: 'Organic Sessions', urlOnly: true },
					users: { label: 'Organic Users', urlOnly: true },
					bounceRate: { label: 'Bounce Rate', lessIsBetter: true, urlOnly: true },
					avgSessionDuration: { label: 'Avg Session Duration', urlOnly: true },
					clicks: { label: 'Clicks' },
					ctr: { label: 'CTR' },
					impressions: { label: 'Impressions' },
					position: { label: 'Avg Position', lessIsBetter: true },
				};
			},

			urlStats() {

				const data = this.selectedURLOverview;

				let stats = [];

				if ( !data ) return { 
					'title': '', 
					'stats': stats 
				};

				Object.keys(this.fieldMeta).forEach( function(key) {

					let stat = { 
						type: key, 
						label: this.fieldMeta[key].label, 
						value: data[key + this.selectedFrequency]
					};

					if ( data[key + this.selectedFrequency + 'Change'] ) {

						stat.change = {};
						
						stat.change.value = general.percentDiff(data[key + this.selectedFrequency ],data[key + this.selectedFrequency + 'History']);
						stat.change.outcome = data[key + this.selectedFrequency + 'Change'] > 0 ? 'good' : 'bad';
						stat.change.direction = data[key + this.selectedFrequency + 'Change'] > 0 ? '+' : '-';
						stat.change.type = 'percentage';
						stat.change.oldValue = 'Previously ' + data[key + this.selectedFrequency + 'History'];

						if ( this.fieldMeta[key].lessIsBetter ) {
							stat.change.outcome = stat.change.outcome === 'good' ? 'bad' : 'good';
						}

					}
					
					stats.push(stat);

				}.bind(this));

				// For URL type, show combined stats (backward compatibility)
				return { 
					'title': 'URL Performance Overview', 
					'stats': stats 
				};
			},

			initLabels() {
				return {
					loadedFilters: "Filters",
					loadedTable: this.theType === "url" ? "URLs" : "Keywords",
					loadedGraph: "Graph",
					loadedProject: "Project info"
				};
			},

			projectName() {
				return !!this.fetchedProject ? this.projectInfo.url.replaceAll("https://", "").replaceAll("/", "") : "";
			},

			fullAccess() {
				return !!this.projectInfo.access && this.projectInfo.access === "full-access";
			},

			tableTitle() {
				let r = {
					urlKeyword:
						!this.table.urlKeyword.loading && !!this.table.urlKeyword.count
							? this.table.urlKeyword.count + " Keyword" + (this.table.urlKeyword.count !== 1 ? "s" : "")
							: "",
				};

				let t = this.type === "url" ? "URL" : "Keyword";

				r.type =
					!this.table[this.type].loading && !!this.table[this.type].count
						? this.table[this.type].count + " " + t + (this.table[this.type].count !== 1 ? "s" : "")
						: "";

				return r;
			},

			initFinished() {
				return !!this.fetchedProject && !!this.graphLoaded && !!this.projectListLoaded;
			},

			type() {
				return this.$route.params.type;
			},

			project() {
				return this.$route.params.projectId;
			},

			showOpportunityFilters() {
				return this.fetchedProject && this.googleAccountConnected && !!this.showMinimized[this.type].filters;
			},

			hasGraph() {
				return Object.keys(this.graphInfo).length > 0;
			},

			showGraph() {
				return this.hasGraph && !!this.showMinimized[this.type].graph && !this.reconnectErrored;
			},

			graphLineTickBoxes() {
				let r = [];

				if (!this.hasGraph) return r;

				Object.keys(this.graphInfo.values).forEach(
					function(fieldName) {
						r.push({
							_id: fieldName,
							label: this.graphInfo.values[fieldName].label,
							styles: {
								color: this.graphInfo.values[fieldName].colour,
								"font-weight": "bold",
							},
							disabled: this.graphLines.length < 2 && this.graphLines.indexOf(fieldName) > -1,
						});
					}.bind(this)
				);

				return r;
			},

			theChart() {
				// Return empty chart if no graph data is available yet
				if (!this.graphInfo.labels || !this.graphInfo.values) {
					return {
						data: {
							labels: [],
							datasets: []
						},
						options: {}
					};
				}

				let slice = this.selectedFrequency - this.selectedFrequency * 2,
					labels = this.graphInfo.labels.slice(slice),
					labels2 = this.graphInfo.labels.slice(slice * 2, slice);

				if (this.showDifferences) {
					labels.forEach(function(label, index) {
						labels[index] = [labels[index], "vs", labels2[index]];
					});
				}

				let maxTicksLimit = Math.floor( document.documentElement.clientWidth / ( this.showDifferences ? 100 : 60 ) );

				let theChart = {
					data: {
						labels: labels,
						datasets: [],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: {
								display: false,
								labels: {
									filter: function(item, chart) {
										return item.text && item.text.trim() !== '';
									}
								}
							},
							tooltip: {
								callbacks: {

									title: function(tooltipItems) {
										if (!tooltipItems.length) {
											return '';
										}
										const tooltipItem = tooltipItems[0];
										const dataset = tooltipItem.dataset;

										const labels = tooltipItem.chart.data.labels[tooltipItem.dataIndex];

										if (dataset.borderDash && dataset.borderDash.length > 0) {
											return Array.isArray(labels) ? labels[2] : labels;
										}

										return Array.isArray(labels) ? labels[0] : labels;
									},
									label: function(context) {

										let dataset = context.dataset,
											index = context.dataIndex;

										if (!!dataset.isAnnotation) {
											let max = 4;

											if (dataset.labels[index].length > max) {
												let labels = dataset.labels[index].slice(0, max);

												labels.push("+" + (dataset.labels[index].length - max) + ' more, click for all events');

												return labels;
											}

											return dataset.labels[index];
										}

										return dataset.label + ": " + context.parsed.y;
									},
									labelColor: function(context) {
										// Access the custom attribute 'showTooltipSquare' for the dataset
										const dataset = context.dataset;

										if (!!dataset.isAnnotation) {
											return {
												borderColor: dataset.pointBackgroundColor,
												backgroundColor: dataset.pointBackgroundColor,
											};
										}

										return {
											borderColor: dataset.borderColor,
											backgroundColor: dataset.backgroundColor,
										};
									},
								},
							},
						},
						scales: {
							x: {
								grid: {
									display: true,
									drawBorder: true,
									drawOnChartArea: false,
								},
								ticks: {
									autoSkip: true,
									maxTicksLimit: maxTicksLimit,
								},
							},
							y2: {
								type: 'linear',
								display: false,  // This hides the second axis
								position: 'right',
								min: 1,         // Sets minimum value to 1
								max: 100,
								ticks: {
								},
								// Grid line settings
								grid: {
									drawOnChartArea: false // only want the grid lines for the first axis
								},
							},
						}
					}
				};

				this.graphLines.forEach(
					function(fieldName, index) {
						++index;

						let field = this.graphInfo.values[fieldName],
							v = field.values;

						v = v.slice(slice);

						// Use simplified scale IDs for Chart.js v3+
						let scaleId = index === 1 ? 'y' : 'y' + index;

						theChart.options.scales[scaleId] = {
							type: "linear",
							display: true,
							position: index % 2 === 0 ? "right" : "left",
							reverse: !!field.reverse,
							min:0,
							grid: {
								display: true,
								drawBorder: true,
								drawOnChartArea: false,
							},
							title: {
								display: true,
								text: field.label,
							},
							ticks: {
							},
						};

						theChart.data.datasets.push({
							label: field.label,
							fill: false,
							backgroundColor: field.colour,
							borderColor: field.colour,
							data: v,
							yAxisID: scaleId,
							spanGaps: false,
							tension: 0.4
						});

						if (this.showDifferences) {
							let v2 = [].concat(field.values);

							v2 = v2.slice(slice * 2, slice);

							theChart.data.datasets.push({
								label: field.label,
								fill: false,
								backgroundColor: field.colour,
								borderColor: field.colour,
								data: v2,
								yAxisID: scaleId,
								borderDash: [5, 5],
								spanGaps: false,
								tension: 0.4
							});
						}
					}.bind(this)
				);

				if (this.graphInfo.annotations) {
					let data = this.graphInfo.annotations.data.slice(slice),
						labels = this.graphInfo.annotations.labels.slice(slice);
					

					let annotations = {
						project: [],
						global: [],
					};

					data.forEach(function(d) {
						let project = d === 2 ? d : null,
							global = d !== 2 ? d : null;

						annotations.project.push(project);
						annotations.global.push(global);
					});

					theChart.data.datasets.unshift({
						label: "",
						fill: false,
						//tooltips: item.tooltips,
						data: annotations.project,
						labels: labels,
						yAxisID: "y2",
						spanGaps: false,
						pointBackgroundColor: "#f1492a",
						pointBorderColor: "#f1492a",
						pointStyle: "triangle",
						borderColor: "rgba(0, 0, 0, 0)",
						isAnnotation: true,
						clip: false,
						showLine: false,
						pointRadius: 6
					});

					theChart.data.datasets.unshift({
						label: "",
						fill: false,
						//tooltips: item.tooltips,
						data: annotations.global,
						labels: labels,
						yAxisID: "y2",
						spanGaps: false,
						pointBackgroundColor: "#394559",
						pointBorderColor: "#394559",
						pointStyle: "triangle",
						borderColor: "rgba(0, 0, 0, 0)",
						isAnnotation: true,
						clip: false,
						showLine: false,
						pointRadius: 6
					});

					if (this.showDifferences) {
						let previousData = this.graphInfo.annotations.data.slice(slice * 2, slice),
							previousLabels = this.graphInfo.annotations.labels.slice(slice * 2, slice),
							annotationsOld = {
								project: [],
								global: [],
							};

						previousData.forEach(function(d) {
							let project = d === 2 ? d : null,
								global = d !== 2 ? d : null;

							annotationsOld.project.push(project);
							annotationsOld.global.push(global);
						});

						theChart.data.datasets.unshift({
							label: "",
							fill: false,
							data: annotationsOld.project,
							labels: previousLabels,
							yAxisID: "y2",
							spanGaps: false,
							pointBackgroundColor: "rgba(241, 73, 42, 0.75)",
							pointBorderColor: "rgba(241, 73, 42, 0.75)",
							pointStyle: "triangle",
							borderColor: "rgba(0, 0, 0, 0)",
							isAnnotation: true,
							borderDash: [5, 5],
							clip: false,
							showLine: false,
							pointRadius: 6
						});

						theChart.data.datasets.unshift({
							label: "",
							fill: false,
							data: annotationsOld.global,
							labels: previousLabels,
							yAxisID: "y2",
							spanGaps: false,
							pointBackgroundColor: "rgba(57, 69, 89, 0.75)",
							pointBorderColor: "rgba(57, 69, 89, 0.75)",
							pointStyle: "triangle",
							borderColor: "rgba(0, 0, 0, 0)",
							isAnnotation: true,
							borderDash: [5, 5],
							clip: false,
							showLine: false,
							pointRadius: 6
						});
					}
				}

				return theChart;
			},

			showRecheck() {
				if (window.location.host === "app.rankcaddy.io") return false;

				return !this.stillGathering && this.project && this.lastCheckedInfoTable;
			},

			showHighlightText() {
				let r = {};

				["url", "keyword", "urlKeyword"].forEach(
					function(t) {
						r[t] = this.highlightText[t] !== "";

						if (r[t]) {
							r[t] = false;

							this.table[t].items.forEach(function(item) {
								if (!!item.highlight) {
									r[t] = true;
								}
							});
						}
					}.bind(this)
				);

				return r;
			},

			projectListCompd() {
				let projects = [];

				if (this.projectListLoaded) {
					["projectList"].forEach(
						function(t, i) {
							this[t].forEach(function(project) {
								let name = project.url,
									p = { shared: i > 0 };

								Object.keys(project).forEach(function(k) {
									p[k] = project[k];
								});

								p.name = name;

								projects.push(p);
							});
						}.bind(this)
					);
				}

				projects.sort((a, b) => (a.name > b.name ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0));

				return projects;
			},

			currentSearch() {
				return this.table[this.theType].search;
			},

			orderByList() {
				let list = [];

				Object.keys(this.fields).forEach(
					function(f) {
						if (
							this.fields[f].searchVars &&
							this.fields[f].searchVars.canOrderBy &&
							this.fields[f].types &&
							this.fields[f].types.indexOf(this.theType) >= 0
						) {
							list.push({ _id: f, label: this.fields[f].value, value: this.fields[f].value });
						}
					}.bind(this)
				);

				return list;
			},

			tableMeta() {
				let meta = {};

				meta.project = this.project || null;
				meta.filterInfo = this.editFilterInfo;
				meta.showChange = this.showDifferences;

				return meta;
			},

			searchTitle() {
				return this.searchTitleOverride || "New Search/Filter";
			},

			searchDescriptionToolTipText() {
				return this.addFieldDescription;
			},

			searchDescriptionToolTipPreText() {
				return "How the search works >>>";
			},

			searchDescription() {
				return this.searchDescriptionOverride || "<strong>How the search works...</strong><br>" + this.addFieldDescription;
			},

			addFieldDescription() {
				return 'Add a field through the dropdown, then click the operators to toggle your search type (e.g. <span style="background-color: #394559; display: inline-block; color: #fff; line-height: 0.8em; padding: 4px; border-radius: 5px; margin: 0 2px 0 0; font-size: 12px;  vertical-align: middle;">contains</span>, <span style="background-color: #394559; display: inline-block; color: #fff; line-height: 0.8em; padding: 4px; border-radius: 5px; margin: 0 2px 0 0; font-size: 12px;  vertical-align: middle;">equals</span> etc.)<br>You can add some fields more than once, each one added must match to get a result, so you adding multiples only works if you are using search types that aren\'t exact such as <span style="background-color: #394559; display: inline-block; color: #fff; line-height: 0.8em; padding: 4px; border-radius: 5px; margin: 0 2px 0 0; font-size: 12px;  vertical-align: middle;">contains</span>. To search for "term a" OR "term b" you can separate with 2 pipes (e.g. term a||term b||term c). Alternatively you can add another filter group - queries will match any of your filter groups.<br>Keep an eye on the "Your Query" box to check you\'ve created the query you were after.';
			},

			searchSetupMultiple() {
				let filters = {};

				Object.keys(this.fields).forEach(
					function(f) {
						if (this.fields[f].types.indexOf(this.theType) >= 0 && this.fields[f].placements.indexOf("filters") >= 0) {
							filters[f] = general.stripObject(this.fields[f].searchVars);

							let group = null;

							if (filters[f].group) {
								if (typeof filters[f].group === "object") {
									if (filters[f].group[this.type]) group = filters[f].group[this.type];
								} else {
									group = filters[f].group;
								}

								if (group) filters[f].group = group;
								else delete filters[f].group;
							}
						}
					}.bind(this)
				);

				return filters;
			},
			searchSetup() {
				return {
					url: {
						urlLowercase: {
							placeholder: "All or part of the URL",
							showLabel: "Search URLs",
							type: "text",
							var: "url",
						},
					},
					keyword: {
						keyword: {
							placeholder: "All or part of the keyword",
							showLabel: "Search keywords",
							type: "text",
							var: "keyword",
						},
						urlLowercase: {
							placeholder: "All or part of the URL",
							showLabel: "Search URLs",
							type: "text",
							var: "url",
						},
					},
					urlKeyword: {
						keyword: {
							placeholder: "All or part of the keyword",
							showLabel: "Search keywords",
							type: "text",
							var: "keyword",
						},
					},
				};
			},

			minimizedList() {
				let labels = {
						graph: {
							svg: ["fas", "chart-line"],
							label: "Graph",
							hide: this.theType === "urlKeyword" || !this.hasGraph,
						},
						filters: {
							svg: ["fab", "searchengin"],
							label: "Search & Opportunity Filters",
						},
						low: {
							svg: "question",
							label: "These numbers seems a bit low",
							hide: this.theType !== "urlKeyword",
							textOnly: true,
							tip:
								"These numbers will match up with what you see in Search Console (at the time of reporting) but they may not match up with the totals you see for this page. This is due to Google anonymising some queries to protect user privacy. These anonymised queries are not shown in the Search Console report, but they are still counted in the total click count for the page.",
						},
					},
					buttons = [];

				if (this.fetchedProject && this.googleAccountConnected && !this.projectInfo.googleAccountFirstTime) {
					Object.keys(labels).forEach(
						function(type) {
							if (!!labels[type].hide) return;

							let tip = !!labels[type].tip ? labels[type].tip : "",
								svg = !!labels[type].svg ? labels[type].svg : "";

							buttons.push({
								type: type,
								label: labels[type].label,
								maximize: !!labels[type].maximize,
								tip: tip,
								textOnly: !!labels[type].textOnly,
								svg: svg,
							});
						}.bind(this)
					);
				}

				return buttons;
			},

			generatingReports () {

				return !!this.table.urlKeyword.scanning;

			},

			generateReportButtonLabel() {
				if (this.isPDF) return "Report unavailable for PDFs";

				if (this.generatingReports) return "Generating Reports...";

				return this.table.urlKeyword.hasPageReports ? "Re-Generate Reports" : "Generate Reports";
			},

			isPDF() {
				return !!this.url && this.url.substring(this.url.length - 4).toLowerCase() === ".pdf";
			},

			generateReportButtonDisabled() {
				return this.isPDF || this.generatingReports;
			},

			googleAccountConnected() {
				if (this.forceReconnect || this.reconnectErrored) return false;

				return !!this.projectInfo && !!this.projectGoogleAccount && !!this.projectInfo.analyticsViewId;
			},

			tableName() {
				return "data-dive--" + this.theType;
			},

			projectAdmin() {
				
				if (!this.projectInfo || !this.projectInfo._id) return false;

				const _id = this.projectInfo._id.toString();

				const p = this.projectListCompd.find((p) => p._id.toString() === _id);
	
				return !!p && !p.shared;
			},

			selectedFilterUniqueName() {
				let n = this.theType === "urlKeyword" ? "urlKeywordSelectedFilter" : "selectedFilter";

				return this[n].index > -1 ? this[n].unique : "";
			},

			selectedFilter() {
				return this.filters[this.type] && typeof this.filters[this.type].index !== "undefined" ? this.filters[this.type] : { index: -1 };
			},

			urlKeywordSelectedFilter() {
				if (this.filters.urlKeyword && typeof this.filters.urlKeyword.index !== "undefined") return this.filters.urlKeyword;

				if (this.filters[this.type] && typeof this.filters[this.type].index !== "undefined" && this.filters[this.type].followThrough)
					return this.filters[this.type];

				return { index: -1 };
			},

			minUrl() {
				return this.minUrlProcess(this.url);
			},

			theType() {
				return !!this.url ? "urlKeyword" : this.type;
			},

			keywordsSelectedProcessed() {
				let ids = [],
					keywords = [],
					keywordsAll = [];

				this.keywordsSelected[this.theType].forEach(
					function(kId) {
						let k = this.table[this.theType].items.find((kw) => kw._id.toString() === kId.toString());

						if (typeof k !== "undefined") {
							if (typeof k.keyword !== "undefined" && keywords.indexOf(k.keyword) < 0) {
								if (!k.inAllProjects) keywords.push(k.keyword);

								keywordsAll.push(k.keyword);
							}

							if (ids.indexOf(k._id.toString()) < 0) {
								ids.push(k._id.toString());
							}
						}
					}.bind(this)
				);

				return { ids: ids, keywords: keywords, keywordsAll: keywordsAll };
			},

			projectGoogleAccount() {
				if (this.project !== null && this.projectListLoaded) {
					let project = this.projectListCompd.find((project) => project._id === this.project);

					if (typeof project !== "undefined" && typeof project.googleAccount !== "undefined" && project.googleAccount !== null) {
						return project.googleAccount;
					}
				}

				return null;
			},

			projectDomain() {
				if (this.project !== null && this.projectListLoaded) {
					let project = this.projectListCompd.find((project) => project._id === this.project);

					if (typeof project !== "undefined") {
						return project.url;
					}
				}

				return null;
			},

			loadingText() {
				return this.type === "keyword" ? "Fetching keywords..." : "Fetching URLs...";
			},

			tableHeadings() {
				return this.tableHeadingObjects(this.type);
			},

			tableHeadingsUrlKeyword() {
				let r = {
					check: { value: "Select", checkBox: true, checkBoxVar: "_id" },
					keyword: { value: "Keyword", sort: 1, colspan: 8, component: PageReport, event: "reRunReport", alwaysVisible: true },
					clicks: { value: "Clicks", sort: 1, emptyCell: "0", component: RankUpDown },
					ctr: { value: "CTR %", sort: -1, emptyCell: "0", component: RankUpDown },
					impressions: { value: "Impressions", sort: -1, emptyCell: "0", component: RankUpDown },
					position: { value: "Avg Pos.", sort: 1, emptyCell: "None", component: RankUpDown },
				};

				r.add = { value: "Tracked Pos.", component: TableCurrentPosition, event: "addToProject" };

				r.runReport = {
					value: "Report",
					emptyCell: "",
					component: TableDataDiveReport,
					event: "runReport",
					valueForFilter: "Report",
					alwaysVisible: false,
					hide: false,
				};

				r.investigate = { value: "", emptyCell: "", component: TableButton, event: "investigate", valueForFilter: "Investigate" };

				return r;
			},

			tableItemsUrlKeyword() {
				let items = [];

				this.table.urlKeyword.items.forEach(
					function(item) {
						if (typeof item.isPageReport === "undefined" || this.viewReports.indexOf(item._id) >= 0) {
							if ( item.runReport ) {
								if ( this.generatingReports && !item.runReport.report ) { 
									item.runReport.label = "Generating...";
									item.runReport.click = false;
								}
							}
							items.push(item);
						}
					}.bind(this)
				);

				return items;
			},

			events() {
				let events = [];

				if (!!this.graphInfo && !!this.graphInfo.annotations.events) {
					this.graphInfo.annotations.events.forEach((event) => {
						let ev = {
							date: general.dateFormat("F jS", event.date),
							events: event.events,
						};

						events.push(ev);
					});
				}

				return events;
			},

			tableItems() {
				if (this.type === "url") return this.table.url.items;

				let items = [];

				this.table.keyword.items.forEach(
					function(item) {
						if (typeof item.isPageReport === "undefined" || this.viewReports.indexOf(item._id) >= 0) {
							items.push(item);
						}
					}.bind(this)
				);

				return items;
			},

			emptyLabel() {
				if (this.newAndQueued) {
					return "Queued to be processed...";
				}

				if (this.stillGathering) {
					return "We are still gathering the data for this project.";
				}

				return this.type === "keyword" ? "There are no keyword opportunities at the moment" : "There are no URL opportunities at the moment";
			},
			shouldPeriodicalCheckProject() {
				return (this.projectInfo && this.projectInfo.googleReCheck) || this.stillGathering || this.newAndQueued;
			},
			stillGathering() {
				return !!this.projectInfo && this.projectInfo.googleGathering;
			},
			newAndQueued() {
				return this.projectInfo && !this.stillGathering && !!this.projectInfo.googleAccountFirstTime && this.googleAccountConnected;
			},
			lastCheckedInfo() {
				if (!this.projectInfo || !this.projectInfo.lastGoogleCheck) return "";

				let tableD = !!this.projectInfo.googleCheckedUpto ? this.projectInfo.googleCheckedUpto : this.projectInfo.lastGoogleCheck;

				let t = this.showGraph
						? "Graph data shown upto "
						: !!this.projectInfo.googleCheckedUpto
						? "Table data shown upto "
						: "Table data last checked ",
					d = this.showGraph ? this.graphInfo.lastChecked : tableD;

				return t + general.timeAgo(d, "M jS 'y", "H:i", "M jS");
			},
			lastCheckedInfoTable() {
				if (!this.projectInfo || !this.projectInfo.lastGoogleCheck) return { full: "", short: "" };

				let d = !!this.projectInfo.googleCheckedUpto ? this.projectInfo.googleCheckedUpto : this.projectInfo.lastGoogleCheck;

				//if ( this.projectInfo.googleReCheck ) return 'Re-checking now...';

				d = general.timeAgo(d, "M jS 'y", "H:i", "M jS");

				return {
					full: (!!this.projectInfo.googleCheckedUpto ? "Table data shown upto " : "Table data last checked ") + d,
					short: "Checked: " + general.timeAgo(this.projectInfo.lastGoogleCheck, "M jS 'y", "H:i", "M jS"),
				};
			},
		},
		methods: {
			...mapMutations("general", ["updateLoadingText", "reloadInstructionalTips"]),
			...mapActions("general", ["loadProjects"]),

			closeInsights () {

				this.showInsights = false;

			},

			openInsights () {

				this.showInsights = true;

			},



			initLoaded ( type ) {
				this.init[type] = true;
				let loading = [];
				Object.keys(this.init).forEach( function ( type ) {
					if ( !this.init[type] ) {
						loading.push(this.initLabels[type]);
					}
				}.bind(this));
				if ( loading.length > 0 ) {
					// Replace last comma with & if there are multiple items
					const loadingText = loading.length > 1 
						? `Loading ${loading.slice(0, -1).join(", ")} & ${loading[loading.length - 1]}`
						: `Loading ${loading[0]}`;
					this.updateLoadingText(loadingText);
				}
				else this.updateLoadingText();
			},

			reMountTable () {

				this.tableRemounted[this.theType] = true;
				//this.tableKey++;

			},

			labelClick (info) {
				if ( info.label.indexOf('click for all events') > -1 ) {
					this.showEventsModal = true;
				}
			},

			switchtype(type) {
				if (this.type === type) return;

				this.$router.push({ name: "data-dive-project", params: { type: type, projectId: this.project } });
			},

			saveFilter(info) {
				this.updateLoadingText(true);

				let d = {
					type: this.theType,
					filters: info.processed.query,
					sortBy: info.processed.orderBy,
					info: info.filterInfo,
					_id: info._id || null,
				};

				ApiService.post("/google/opportunities/filters/" + this.project, d)
					.then(
						function(info) {
							this.updateLoadingText();

							general.$snotify(this, "success", (d._id ? "Edit" : "Add") + " Filter", info.data.msg);

							this.editFilterInfo = info.data.info;
							this.searchTitleOverride = info.data.info.name;

							++this.opportunityFiltersKey;

							this.loadFieldsAndFilters();
						}.bind(this)
					)
					.catch(
						function(err) {
							this.updateLoadingText();
							general.$snotify(this, "error", (d._id ? "Edit" : "Add") + " Filter", err);
						}.bind(this)
					);
			},

			filtersChanged() {
				if (!this.tableMeta.filterInfo || !this.tableMeta.filterInfo._id) {
					this.searchTitleOverride = "";
				}

				this.searchDescriptionOverride = "";
				this.showSaveFilter = true;
			},

			closeSearchMultiple(info) {
				const filterChanged = !!info && !!info.filterWasChanged;

				const up = new Date().getTime();

				Vue.set(this.table[this.theType], "lastUpdate", up);

				if (info) {
					this.filterQueryTitle[this.theType] = filterChanged ? "" : this.searchTitleOverride;
					this.filterQueryDescription[this.theType] = filterChanged ? "" : this.searchDescriptionOverride;

					if (Array.isArray(info) && info.length === 0) {
						this.filterQueryTitle[this.theType] = "";
						this.filterQueryDescription[this.theType] = "";

						if (this.theType === "urlKeyword" && this.clearParent) {
							//Vue.set( this.table.url, 'search', [] );
							//Vue.set( this.table.url, 'search2', null );
							this.loadOpportunities("url", null, null, "closeSearchMultiple");
							this.clearParent = false;
						}

						this.search(info, null, true, "closeSearchMultiple");
					} else if (filterChanged || (!!this.editFilterInfo && !!this.editFilterInfo.runOnSearchWithFilterChange)) {
						if (this.editFilterInfo) this.editFilterInfo.runOnSearchWithFilterChange = false; 
						this.search(info, null, true, "closeSearchMultiple");
					}
				} else {
					if (this.table[this.theType].search2 && Object.keys(this.table[this.theType].search2).length > 0) {
						let s2 = general.stripObject(this.table[this.theType].search2);

						Vue.set(this.table[this.theType], "search", {});

						Object.keys(s2).forEach(
							function(k) {
								Vue.set(this.table[this.theType].search, k, s2[k]);
							}.bind(this)
						);
					}
				}
			},

			minimize(info,onAndOff=false) {
				if (typeof info === "string") info = { type: info };

				switch (info.type) {
					case "filters":
					case "graph":

						if ( onAndOff || !this.showMinimized[this.theType][info.type] ) {

							Vue.set(this.showMinimized[this.theType], info.type, !this.showMinimized[this.theType][info.type]);

						}

						if ( this.showMinimized[this.theType][info.type] ) {

							setTimeout( () => {
								general.scrollIntoView( 'the-'+info.type );
							}, 100 );

						}

						break;
				}

			},

			loadGraph(callback) {
				this.graphInfo = {};
				ApiService.get("google/graph/" + this.project)
					.then(
						function(info) {
							this.graphInfo = info.data;

							this.graphLoaded = true;

							this.initLoaded("loadedGraph");

							if (typeof callback !== "undefined") callback();
						}.bind(this)
					)
					.catch(function(err) { // redirectToProject
						if ( err.response.data.error.redirectToProject ) {
							this.$router.push( '/data-dive/' + err.response.data.error.redirectToProject + '/' + this.type );
						}
						this.initLoaded("loadedGraph");
					}.bind(this));
			},

			loadProject(reCheck) {
				if (!reCheck) {
					this.fetchedProject = false;

					Vue.set(this, "projectInfo", {});
					this.brandIgnoreSetting = null;
				}

				ApiService.get("projects/" + this.project)
					.then(
						function(info) {
							this.reCheckDataClicked = false;

							let working = false;

							if ( !!this.projectListLoaded ) this.initLoaded("loadedProject");

							if (info.data) {
								working = info.data.googleReCheck || info.data.googleGathering;

								if (!reCheck || (!!reCheck && !working)) {
									Vue.set(this, "projectInfo", info.data);

									if (!this.hasHashFilter) {
										this.loadOpportunities(null, null, null, "loadProject");
									}

									this.hasHashFilter = false;
									this.brandIgnoreSetting = !!info.data.brandIgnoreSetting && info.data.brandIgnores.length > 0;
								}
							} else {
								Vue.set(this, "projectInfo", null);
							}

							this.fetchedProject = true;

							if (working) {
								this.clearTimeout();

								this.loadProjectTimeout = setTimeout(
									function() {
										this.loadProject(true);
									}.bind(this),
									10000
								);
							} else if (reCheck) {
								this.loadOpportunities(null, null, null, "loadProject reCheck");
							}
						}.bind(this)
					)
					.catch(
						function(err) {
							Vue.set(this, "projectInfo", null);
							this.updateLoadingText();
							this.reCheckDataClicked = false;
						}.bind(this)
					);
			},

			investigate(info) {
				this.investigations = [];

				let items;

				if (typeof info === "undefined") {
					items = this.keywordsSelectedProcessed.ids;
				} else {
					items = [info];
				}

				items.forEach(
					function(id) {
						id = typeof info === "undefined" ? id : id._id;

						let k = this.table[this.theType].items.find((kw) => kw._id.toString() === id.toString());

						if (k) {
							let item = { _id: k._id, url: k.url.link };

							if (typeof k.keyword !== "undefined") item.keyword = k.keyword;

							this.investigations.push(item);
						}
					}.bind(this)
				);

				this.showInvestigate = true;
			},

			closeInvestigateModal(info) {
				general.modalClose();

				this.showInvestigate = false;
			},

			brandIgnoreSettingChange(info) {

				if ( !!info.forceLoad && this.projectInfo.brandIgnoreSetting === info.brandIgnoreSetting ) {
					this.loadOpportunities(this.theType, null, null, "brandIgnoreSettingChange");
				}

				if ( this.theType === "urlKeyword" && ( !!info.forceLoad || this.projectInfo.brandIgnoreSetting !== info.brandIgnoreSetting ) ) {
					this.loadOpportunities('url', null, null, "brandIgnoreSettingChange urlKeyword");
				}

				Vue.set(this.projectInfo, "brandIgnoreSetting", info.brandIgnoreSetting);
				
			},

			minUrlProcess(url) {
				if (url !== "") {
					try {
						let urlInfo = new URL(url);

						if (urlInfo.pathname !== "" && urlInfo.pathname !== "/") {
							return "..." + urlInfo.pathname;
						}

						return url;
					} catch (e) {
						let url2 = url.replace(this.projectDomain, "");

						return url2 === "/" || url2 === "" ? url : "..." + url2;
					}
				}

				return "";
			},

			reRunReport(info) {
				this.runReport(info, true, "urlKeyword");
			},

			runReportUrlKeyword(info) {
				this.runReport(info, false, "urlKeyword");
			},

			runReport(info, force, type) {
				if (this.generateReportButtonDisabled) return;

				type = typeof type !== "undefined" ? type : "keyword";

				if (typeof info.label !== "undefined" && info.label === "View/Hide Report") {
					const index = this.viewReports.indexOf(info._id);

					if (index > -1) {
						this.viewReports.splice(index, 1);
					} else {
						this.viewReports.push(info._id);
					}

					return;
				}

				this.updateLoadingText("Running scan...");

				let forceName = "force",
					isAll = info._id === "all";

				if (isAll) {
					info._id = this.tableItemsUrlKeyword[0]._id;

					forceName = "force-all";
				}

				if (!info.hasReport) force = true;

				let url = "google/keywords/run-report/" + info._id + "/" + (typeof force !== "undefined" ? forceName : "cached");

				if (this.project) {
					url =
						"google/keywords/run-report/" +
						info._id +
						"/project/" +
						this.project +
						"/" +
						(typeof force !== "undefined" ? forceName : "cached");
				}

				ApiService.get(url)
					.then(
						function(response) {
							if (response.data.message) {
								general.$snotify(this, "success", "Page Reports", response.data.message);
							} else {
								this.updateLoadingText("Scan complete, fetching results...");
							}

							this.loadOpportunities(
								type,
								function() {
									this.updateLoadingText();
								}.bind(this),
								null,
								"runReport"
							);
						}.bind(this)
					)
					.catch(
						function(err) {
							general.$snotify(this, "error", "Page Report", err);
							this.updateLoadingText();
						}.bind(this)
					);
			},

			searchFilters(filterInfo) {
				filterInfo = general.stripObject(filterInfo);

				this.showSaveFilter = !!filterInfo.info._id;
				this.editFilterInfo = filterInfo.info || {};
				this.searchTitleOverride = filterInfo.info.name;
				this.searchDescriptionOverride =
					(filterInfo.info.filterTips && filterInfo.info.filterTips.replace("{days}", this.selectedFrequency)) || "";

				let sortBy = Array.isArray(filterInfo.info.sortBy) ? filterInfo.info.sortBy : [filterInfo.info.sortBy];

				let s = { query: filterInfo.info.filters, orderBy: sortBy };

				if (!!filterInfo.info.highlight) s.highlight = filterInfo.info.highlight;

				Vue.set(this.highlightText, this.theType, !!filterInfo.info.highlightText ? filterInfo.info.highlightText : "");

				Vue.set(this.table.urlKeyword, "search", []);

				Vue.set(this.table[this.theType], "search", s);

				if (this.theType === "url" && filterInfo.action === "run" && !!filterInfo.info.followThrough) {
					Vue.set(this.table.urlKeyword, "search", general.stripObject(this.table[this.theType].search));
				}

				Vue.set(this.triggerSomething, this.theType, "");

				setTimeout(
					function() {
						if (filterInfo.action === "run") {
							this.editFilterInfo.instantClose = true;
							Vue.set(this.triggerSomething, this.theType, "multipleSearchModal");
							this.filterQueryTitle[this.theType] = filterInfo.info.name;
							this.filterQueryDescription[this.theType] =
								(filterInfo.info.filterTips && filterInfo.info.filterTips.replace("{days}", this.selectedFrequency)) || "";
							this.filterQueryDescription.urlKeyword =
								(filterInfo.info.filterTipsUrlKeyword &&
									filterInfo.info.filterTipsUrlKeyword.replace("{days}", this.selectedFrequency)) ||
								this.filterQueryDescription[this.theType];
							this.editFilterInfo.runOnClose = true;
							this.loadOpportunities(filterInfo.type, null, true, "searchFilters");
							setTimeout(
								function() {
									this.editFilterInfo.instantClose = false;
								}.bind(this),
								100
							);
						} else {
							// edit

							this.editFilterInfo.runOnClose = false;
							this.editFilterInfo.runOnSearchWithFilterChange = true;
							Vue.set(this.triggerSomething, this.theType, "multipleSearchModal");
						}
					}.bind(this),
					filterInfo.action === "run" ? 10 : 200
				);
			},

			tableHeadingObjects(type) {
				let h = {};

				if (type === "keyword") {
					h = {
						check: { value: "Select", checkBox: true, checkBoxVar: "_id" },
						keyword: {
							value: "Keyword",
							sort: 1,
							forFilter: true,
							filterText: true,
							colspan: 9,
							component: PageReport,
							event: "reRunReport",
							alwaysVisible: true,
						},
						url: {
							value: "URL",
							sort: -1,
							component: LinkWithIcon,
							forFilter: true,
							filterText: true,
							event: "viewKeywords",
							alwaysVisible: true,
						},
						clicks: { value: "Clicks", sort: -1, emptyCell: "0", component: RankUpDown },
						ctr: { value: "CTR %", sort: -1, emptyCell: "0", component: RankUpDown },
						impressions: { value: "Impressions", sort: -1, emptyCell: "0", component: RankUpDown },
						position: { value: "Avg Pos.", sort: 1, emptyCell: "None", component: RankUpDown },
						add: { value: "Tracked Pos.", component: TableCurrentPosition, event: "addToProject" },
						runReport: { value: "Report", emptyCell: "", component: TableDataDiveReport, event: "runReport", valueForFilter: "Report" },
						investigate: { value: "", emptyCell: "", component: TableButton, event: "investigate", valueForFilter: "Investigate" },
					};
				} else {
					h = {
						check: { value: "Select", checkBox: true, checkBoxVar: "_id" },
						url: { value: "URL", sort: 1, component: LinkWithIcon, forFilter: true, alwaysVisible: true },
						keywordCount: {
							value: "Keyword Count",
							sort: -1,
							emptyCell: "0",
							component: LinkWithIcon,
							event: "viewKeywords",
							forFilter: true,
						},
						sessions: { value: "Organic Sessions", sort: -1, emptyCell: "0", component: RankUpDown },
						users: { value: "Organic Users", sort: -1, emptyCell: "0", component: RankUpDown },
						bounceRate: { value: "Bounce Rt. %", sort: -1, emptyCell: "None", component: RankUpDown },
						avgSessionDuration: { value: "Avg Sess. MM:SS", sort: -1, emptyCell: "None", component: RankUpDown },
						clicks: { value: "Clicks", sort: -1, emptyCell: "0", component: RankUpDown },
						ctr: { value: "CTR %", sort: -1, emptyCell: "0", component: RankUpDown },
						impressions: { value: "Impressions", sort: -1, emptyCell: "0", component: RankUpDown },
						position: { value: "Avg Pos.", sort: 1, emptyCell: "None", component: RankUpDown },
					};

					if (type === "all") {
						h.add = { value: "Tracked Pos.", component: TableCurrentPosition, event: "addToProject" };
					}

					h.investigate = { value: "", emptyCell: "", component: TableButton, event: "investigate", valueForFilter: "Investigate" };

					if (this.project !== null && this.projectListLoaded) {
						let project = this.projectListCompd.find((project) => project._id === this.project);

						if (typeof project !== "undefined") {
							if (!!project.averageGoalConversionRateAll90) {
								h.goalCompletionsAll = { value: "Goal Comp.", sort: -1, emptyCell: "0", component: RankUpDown };
								h.goalConversionRateAll = { value: "Goal Conv.", sort: -1, emptyCell: "0", component: RankUpDown };
							}

							if (!!project.averageTransactionsPerSession90) {
								h.transactions = { value: "Trans.", sort: -1, emptyCell: "0", component: RankUpDown };
								h.transactionsPerSession = { value: "Trans. Per Sess.", sort: -1, emptyCell: "0", component: RankUpDown };
								h.transactionRevenue = { value: "Trans. Rev.", sort: -1, emptyCell: "0", component: RankUpDown };
							}
						}
					}
				}

				return h;
			},

			addToProject(info) {
				this.addToProjectProjectIds = [];

				info.projects.forEach(
					function(p) {
						if (p.access === "full-access" && !p.currentPosition) this.addToProjectProjectIds.push(p.projectId);
					}.bind(this)
				);

				this.keywordActions([info._id]);
				this.showKeywordModal = true;
			},

			closeKeywordModal(info) {
				general.modalClose();

				this.showKeywordModal = false;

				let type = this.url !== "" ? "urlKeyword" : "keyword";

				if (typeof info !== "undefined") {
					this.loadOpportunities(type, null, null, "closeKeywordModal");
					Vue.set(this.keywordsSelected, type, []);
				}

				if (this.addToProjectProjectIds.length) {
					this.addToProjectProjectIds = [];
					Vue.set(this.keywordsSelected, type, []);
				}
			},

			closePageReports(info) {
				general.modalClose();

				this.showPageReports = false;
			},

			keywordActions(ids) {
				let type = this.url !== "" ? "urlKeyword" : this.type;

				Vue.set(this.keywordsSelected, type, ids);
			},

			queryString(type, isDownloadType, currentPage) {
				type = typeof type !== "undefined" ? type : this.theType;

				let i = 0,
					queryString = [];

				if (type === "urlKeyword") {
					queryString.push("url=" + encodeURIComponent(this.urlActual || this.url));
				}

				if (!isDownloadType) {
					queryString.push("page=" + this.table[type].page);
				} else {
					if (currentPage) queryString.push("page=" + currentPage);

					queryString.push("download=" + isDownloadType);

					if (isDownloadType === "pdf") {
						queryString.push("pdfTitle=" + this.pdfTitle);
					}

					queryString.push("headings=" + general.stripObject(this.tableHeadingVisibility["data-dive--" + this.theType] || []));

					if (this.showDifferences) queryString.push("history=1");
				}

				if (this.brandIgnoreSetting) queryString.push("brandIgnore=1");

				if (queryString.length > 0) return "?" + queryString.join("&");

				return "?";
			},
			sorting(type) {
				type = typeof type !== "undefined" ? type : this.theType;

				let sorted = this.table[type].sorted;

				if (this.tableSortingInfo["data-dive--" + type]) {
					sorted = this.tableSortingInfo["data-dive--" + type];
				}

				let sortBy = Object.keys(sorted)[0],
					sortDirection = sorted[sortBy] === 1 ? "asc" : "desc";

				return { sortBy: sortBy, sortDirection: sortDirection };
			},

			close() {
				general.modalClose();
				this.keywordsSelected.urlKeyword = [];
				this.selectedUrlId = null;

				this.url = "";

				Vue.set(this.filters, "urlKeyword", {});
				Vue.set(this.table.urlKeyword, "page", 1);
				Vue.set(this.table.urlKeyword, "search", []);
				Vue.set(this.table.urlKeyword, "firstLoad", true);
			},

			sort(info, type) {
				type = type || this.theType;
				this.table[type].page = 1;
				this.table[type].sorted = info;
				if (info.init) {
					delete this.table[type].sorted.init;
					return;
				}
				this.loadOpportunities(type, null, null, "sort");
			},

			search(info, type, scrollTo, label) {
				type = type || this.theType;

				this.table[type].page = 1;
				
				Vue.set(this.table[type], "search", info);

				if (info.orderBy && info.orderBy.length > 0) {
					let s = {};
					s[info.orderBy[0].field] = info.orderBy[0].direction;

					Vue.set(this.table[type], "sorted", s);
				}
				
				this.searchTitleOverride = '';
				this.searchDescriptionOverride = '';
				
				this.loadOpportunities(type, null, scrollTo, "search");
			},

			pagination(info, type) {
				type = type || this.theType;
				this.table[type].page = info.page;
				this.loadOpportunities(type, null, null, "pagination");
			},

			viewKeywords({ url, urlActual, _id }) {
				this.url = url;
				this.urlActual = urlActual || null;
				this.selectedUrlId = _id;
				this.loadOpportunities("urlKeyword", null, null, "viewKeywords");
			},

			reCheckData() {
				if (this.reCheckDataClicked) return;

				if (this.projectInfo.googleReCheck) {
					general.$snotify(this, "error", "Re-Check Data", "Your data is already being checked.");

					return;
				}

				if (!!this.projectInfo && !this.projectInfo.canRecheckGoogle) {
					if (this.projectInfo.googleAccountInvalid) {
						general.$snotify(
							this,
							"error",
							"Re-Check Data",
							"We have been having issues getting data from your Google account, please contact support"
						);

						return;
					}

					general.$snotify(this, "error", "Re-Check Data", "You must leave 24 hours between re-checking your data");

					return;
				}

				this.reCheckDataClicked = true;

				this.updateLoadingText(true);

				ApiService.get("google/re-check-project/" + this.project)
					.then(
						function(info) {
							this.loadProject();
							this.updateLoadingText();
							general.$snotify(this, "success", "Re-check Data", info.data);
						}.bind(this)
					)
					.catch(
						function(err) {
							general.$snotify(this, "error", "Re-check Data", err);
							this.updateLoadingText();
						}.bind(this)
					);
			},

			tableMounted() {
				this.reloadInstructionalTips();
			},

			closeReportOptions(fileType, page, pdfTitle) {
				if (fileType) {
					this.pdfTitle = pdfTitle;

					this.loadOpportunities(this.theType, null, null, "download", fileType, !!page ? page : null);
				}

				this.showReport = false;

				general.modalClose();
			},

			download(fileType) {
				this.$dialog
					.confirm("Are you sure you want to create a " + fileType.toUpperCase() + "?", { okText: "Yes", cancelText: "Cancel" })
					.then(
						function() {
							if (this.table[this.theType].pages > 1 || fileType === "pdf") {
								this.showReport = fileType;
							} else {
								this.loadOpportunities(this.theType, null, null, "download", fileType);
							}
						}.bind(this)
					)
					.catch(function() {});
			},

			loadOpportunities(type, callback, scrollTo, label, isDownloadType, currentPage) {
				type = type || this.theType;

				if (this.project === null) return;

				if (!isDownloadType) this.table[type].loading = true;
				else this.updateLoadingText("Generating " + isDownloadType.toUpperCase() + "...");

				let type2 = type,
					s = general.stripObject(this.table[type].search),
					vars = {
						filters: s,
					};

				if (s.query) {
					s.query = Array.from(s.query);

					s.query.forEach(
						function(k) {
							let f = Object.keys(k)[0].split("{days}");

							if (f.length === 2 && ["Drop", "Climb"].indexOf(f[1]) >= 0) {
								this.showDifferences = true;
							}
						}.bind(this)
					);
				}

				ApiService.post(
					"google/opportunities/" +
						type2 +
						"/" +
						this.project +
						"/" +
						this.selectedFrequency +
						this.queryString(type, isDownloadType, currentPage),
					vars
				)
					.then(
						function(info) {
							this.initLoaded("loadedTable");

							if (!!isDownloadType) {
								const type = info.headers["content-type"];
								const blob = new Blob([info.data], { type: type, encoding: "UTF-8" });
								const link = document.createElement("a");
								link.href = window.URL.createObjectURL(blob);
								link.download = this.projectName + "." + isDownloadType;
								link.click();
								this.updateLoadingText();
								return;
							}

							let firstLoad = this.table[type].firstLoad,
								wasScanning = !!this.table[type].scanning,
								lastUpdate = this.table[type].lastUpdate;

							if ( type === "urlKeyword" && !!this.selectedUrlId ) {
								this.selectedURLOverview = info.data.overview;
							}

							Vue.set(this.table, type, info.data);

							Vue.set(this.table[type], "lastUpdate", lastUpdate);

							Vue.set(this.table[type], "firstLoad", firstLoad);

							this.table[type].search = s;

							Vue.set(this.table[type], "loading", false);

							this.table[type].firstLoad = false;

							this.hasDays = info.data.hasDays;

							this.reMountTable();

							Vue.set(this.projectInfo, "analyticsViewId", info.data.analyticsViewId);

							if (callback) callback();

							if (type === "urlKeyword" && this.url !== "") {
								if (this.table.urlKeyword.scanning) {
									if (this.loadOpportunitiesTimeout) clearTimeout(this.loadOpportunitiesTimeout);

									this.loadOpportunitiesTimeout = setTimeout(
										function() {
											this.loadOpportunities("urlKeyword", null, null, "loadOpportunities setTimeout?");
										}.bind(this),
										10000
									);
								} else if (wasScanning) {
									general.$snotify(this, "success", "Page Reports", "Your reports are complete.");
								}
							} else if (scrollTo) {
								this.$refs.mainTable.$el.scrollIntoView();
							}
						}.bind(this)
					)
					.catch(
						function(err) {
							this.table[type].loading = false;

							general.$snotify(this, "error", "Data Dive", err);

							this.filterQueryTitle[this.theType] = "";
							this.filterQueryDescription[this.theType] = "";
							Vue.set(this.table[this.theType], "search", []);

							if (!!isDownloadType) {
								this.updateLoadingText();
							}
						}.bind(this)
					);
			},

			googleConnectComplete(info) {
				this.$snotify[info.status](info.msg, "Google");

				if (info.status === "success") {
					this.loadedOpportunitiesAfterProjects = true;

					this.loadProjects({ force: true });

					if (this.forceReconnect || this.reconnectErrored) {
						this.loadProject();

						this.forceReconnect = false;
						this.reconnectErrored = false;
					}
				}
			},
			clearTimeout() {
				if (this.loadProjectTimeout > 0) clearTimeout(this.loadProjectTimeout);

				this.loadProjectTimeout = 0;
			},
			loadFieldsAndFilters(callback) {
				ApiService.get("fields/data-dive/" + this.project + "/url,urlKeyword,keyword")
					.then(
						function(info) {
							this.initLoaded("loadedFilters");
							this.fields = info.data.fields.fields;
							this.comparisons = info.data.fields.comparisons;
							this.opportunityFilters = info.data.filters;
							this.opportunityFiltersLoaded = true;

							if (typeof callback !== "undefined") callback();
						}.bind(this)
					)
					.catch(function(err) {}.bind(this));
			},
		},
		created() {
			let filter = window.location.hash.replace("#", "");

			if (filter) {
				filter = filter.split("--");

				this.showMinimized[this.type].graph = false;

				this.hasHashFilter = true;
			}

			this.loadFieldsAndFilters(
				function() {
					let foundHashFilter = this.opportunityFilters[this.type].preset.find((f) => f.unique === filter[0]);

					if (foundHashFilter) {
						if (filter.length === 2 && !isNaN(filter[1])) {
							let days = parseInt(filter[1]);

							if (this.frequencies.indexOf(days) >= 0) {
								this.selectedFrequency = days;
							}
						}

						this.searchFilters({ info: foundHashFilter, action: "run", clear: false, from: "created" });
						foundHashFilter = null;
					} else {
						this.hasHashFilter = false;
						this.showMinimized[this.type].graph = true;
					}

					this.loadProject();
				}.bind(this)
			);

			this.loadGraph();
		},
		beforeDestroy() {
			this.clearTimeout();
		},
	};
</script>

<style lang="less" scoped>
	@import "./../assets/css/less/_settings.less";
	@import "./../assets/css/less/_helper.less";
	@imgRoot: "./../";
	.google-connect-data-dive {
		margin-bottom: 20px !important;
	}
	.url-keyword-buttons {
		background-color: #fff;
		text-align: center;
		margin: 0 12px 20px;
		padding: 10px 0 0;
		.rounded(5px);
		.view {
			font-size: 12px;
			padding: 0;
			margin: 0 0 2px;
		}
		.large-button {
			margin: 0 4px 10px;
			&.active {
				background-color: @formDefaultColour !important;
				cursor: default;
			}
		}
	}
	.type-switch {
		strong {
			cursor: pointer;
			.trans;
			&:hover {
				color: @formDefaultColour;
			}
		}
	}
	.graph-checkbox-wrap {
		margin-bottom: 15px;
		padding-right: 180px;
	}
	.graph-margin-bottom {
		margin-bottom: 20px;
	}
	.differences-switcher {
		margin-top: 1px !important;
	}
	.highlight-text {
		background: url("./../assets/img/icons/star-solid.svg") 11px center no-repeat;
		background-size: 18px auto;
		padding: 2px 10px 0 36px;
		margin: 0 0 18px;
	}
	.svg {
		background-color: @darkContrast;
		vertical-align: top;
		width: 20px;
		height: 20px;
		display: inline-block;
		padding: 4px;
		position: relative;
		.rounded(50%);
		.trans;
		cursor: pointer;

		svg {
			fill: #fff;
			color: #fff;
			width: 50%;
			display: block;
			position: absolute;
			left: 25%;
			top: 10%;
			.trans;
		}

		&.minimize {
			position: absolute;
			top: 11px;
			right: 11px;
			z-index: 2;
		}

		&:hover {
			background-color: #fff;

			svg {
				fill: @darkContrast;
				color: @darkContrast;
			}
		}
		&.white {

			background-color: #fff;

			svg {

				fill: @darkContrast;
				color: @darkContrast;

			}

		}
	}
	.last-checked {
		margin: 0;
		padding: 0;
		font-size: 12px;

		&.for-table {
			margin: 10px 0;

			span {
				display: inline-block;
				width: 50%;
			}

			.description {
				color: @formDefaultColour;
				font-weight: @bold;
			}

			.checked {
				text-align: right;
			}
		}
	}
	.frequency-setters {
		text-align: center;
		margin-bottom: 0;
		position: relative;
		z-index: 2;
		background-color: #394559;
		padding-top: 20px;

		&.in-modal {

			padding: 10px;

		}

		> span {
			display: inline-block;
			background-color: @darkContrast;
			padding: 3px 8px;
			.rounded(5px);
			font-size: 12px;
			color: #fff;
			cursor: pointer;
			margin-left: 10px;
			.trans;

			&:hover,
			&.selected {
				background-color: @formDefaultColour;
			}
		}
	}

	.action-button {
		background-color: @formDefaultColour;
		color: #fff;
		display: inline-block;
		.rounded(4px);
		font-size: 14px;
		cursor: pointer;
		margin: 0;
		padding: 2px 6px;
		&:hover {
			color: @formDefaultColour;
			background-color: #fff;
		}
		&.right {
			float: right;
		}
	}

	.large-button {
		&.process-reports {
			margin: 10px auto;
			display: block;
			width: 200px;
		}
		&.insights {
			margin: 0 !important;
		}
	}

	.recheck, .insights-btn-wrap {
		margin: 15px auto 0;
		display: block;
		width: 140px;
		.large-button {
			margin: 0 !important;
		}
		&.innactive {
			.large-button {
				cursor: default;
				.opacity(0.5);
			}
		}
		p {
			font-size: 10px;
			padding: 0;
			margin: 0;
			text-align: center;
		}
	}

	.action-buttons {
		position: fixed;
		background-color: rgba(255, 255, 255, 0.5);
		bottom: 0;
		left: 0;
		width: 100%;
		padding: 10px 10px 10px 24px;
		z-index: 99;
		.clearfix;
		.action-button {
			margin-right: 6px;
		}
	}
	.select-a-project {
		background-color: #fff;

		p {
			margin: 0;
			padding: 30px 20px;
			text-align: center;
			color: @formDefaultColour;
		}
	}

	.show-headings {
		background-color: @darkContrast;
		border-radius: 5px;
		margin-bottom: 20px;
		padding: 10px;

		.show-headings-heading {
			font-size: 16px;
			margin-bottom: 5px;
			color: #fff;
		}

		.show-headings-wrap {
			display: inline-block;
			margin: 0 30px 0 0;

			.show-headings-sub-heading {
				font-size: 14px;
				margin-bottom: 5px;
			}
		}
	}

	.project-dropdown-wrap {
		margin: 25px 10px;
		position: relative;
		height: 36px;

		.project-dropdown-wrap-inner {
			width: 100%;
			position: absolute;
			background-color: #fff;

			.v-select {
				.vs__clear {
					display: none;
				}
			}
		}
	}

	.still-gathering {
		margin: 15px 0;
		&.error {
			background-color: @red !important;
			.reconnect {
				font-weight: @bold;
				text-decoration: underline;
				cursor: pointer;
			}
		}
	}

	@media screen and (min-width: 540px) {
		.event-actions {
			position: absolute;
			right: 40px;
			top: 10px;
		}
	}

	@media screen and (min-width: 620px) {
		.project-dropdown-wrap {
			&.has-recheck {
				width: 70vw;
			}
		}
	}

	@media screen and (min-width: 768px) {
		.highlight-text {
			margin: 0 0 10px;
		}
		label {
			color: #fff !important;
			opacity: 1 !important;
		}
		.show-headings {
			background-color: @darkContrast;
			border-radius: 5px 5px 0 0;
			margin-bottom: 0;
			position: relative;
			z-index: 2;

			.show-headings-wrap {
				.show-headings-sub-heading {
					color: #fff;
				}
			}
		}
		.recheck {
			top: 144px;
		}

		.project-dropdown-wrap {
			&.has-recheck {
				width: 64vw;
			}
		}
	}
	@media screen and (min-width: 960px) {
		.highlight-text {
			margin: 20px 12px 10px;
		}
	}
	@media screen and (min-width: 1024px) {
		.graph-checkbox-wrap {
			padding-right: 200px;
		}
		.url-keyword-buttons {
			margin: 0;
			padding: 4px 6px 0;
			.view {
				font-size: 10px;
			}
			.large-button {
				margin: 0 2px 10px;
			}
		}
		.last-checked {
			color: #fff;
			position: relative;
			top: -2px;
		}
		.differences-switcher[data-v-3ddf64c5] {
			margin-right: 0 !important;
		}
		.frequency-setters {
			text-align: right;
			position: absolute;
			top: 8px;
			right: 8px;
			margin-bottom: 0;
			padding: 0;
			&.in-modal {
				top: 35px;
				right: 30px;
				padding: 0;
			}
			> span {
				background-color: #fff;
				color: @darkContrast;
				&:hover,
				&.selected {
					color: #fff;
				}
			}
		}
		.large-button {
			&.process-reports {
				display: inline-block;
				margin: 0;
				position: absolute;
				z-index: 4;
				top: 16px;
				right: 60px;
				width: auto;
			}
		}
		.project-dropdown-wrap {
			position: absolute;
			width: 400px;
			right: 24px;
			top: 74px;
			z-index: 3;
			margin: 0;
			.project-dropdown-wrap-inner {
				max-width: 380px;
				right: 0;
			}
		}
		.action-buttons {
			left: 66px;
		}
		.modal {
			.action-buttons {
				left: 0;
			}

			.modal-inner {
				.modal-title {
					padding-right: 315px;
				}
			}
		}
		.recheck, .insights-btn-wrap {
			position: absolute;
			top: 8px;
			z-index: 3;
			right: 24px;
			width: auto;
		}
		.recheck {
			right: 150px;
		}
	}
	@media screen and (min-width: 1240px) {
		.url-keyword-buttons {
			margin: 0 14px 0 0;
		}
	}


</style>